import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCwIcon, CalendarDaysIcon, NetworkIcon, LockIcon, CheckIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function ProgramManagementPage() {
  return <div className="w-full" data-id="element-2284" data-mp="z5iumlzdzyea">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern" data-id="element-2285" data-mp="z629tuxnk8om">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-2286" data-mp="u6y1f6ijyezc">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-2287" data-mp="18v2d3qj6tq8m">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto text-white" data-id="element-2288" data-mp="18x5ch168cvq0">
              Mission-Critical Program Management
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2289" data-mp="gdgkzul5xidj"></div>
            <p className="text-xl text-nrs-light font-bold mb-4 max-w-2xl mx-auto" data-id="element-2290" data-mp="bf597ebb4xso">
              Agile and Traditional Program Delivery for Federal Government and
              Enterprises
            </p>
            <p className="text-lg text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-2291" data-mp="2s3uaj9kczv81">
              New River Systems delivers expert program management services for
              complex federal IT programs in secure government environments. Our
              cleared program managers, Scrum Masters, and PMO specialists have
              proven track records delivering ServiceNow, ICAM, cloud migration,
              and mission-critical IT modernization programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2292" data-mp="2d7czkjg30im0">
              <Link to="/contact-us" className="btn-primary" data-id="element-2293" data-mp="2i7hieg4gppfe">
                Discuss Your Program
              </Link>
              <Link to="/services/program-and-project-management/pmo" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-2294" data-mp="3omi3trvv2p7q">
                View PMO Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICE DELIVERY MODELS */}
      <section className="section-white" data-id="element-2295" data-mp="1pi0afmhrqdq5">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2296" data-mp="23i2mgs3eacxh">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2297" data-mp="23g88jxqjjl2w">
            Program Management Approaches
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-2298" data-mp="1atnbcgndwerf"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2299" data-mp="1arasm19sy0db">
            {/* Agile */}
            <div className="card flex flex-col h-full" data-id="element-2300" data-mp="15shgoda5yhf9">
              <RefreshCwIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2301" data-mp="1kki5s8asakny" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2302" data-mp="1fs89maj2sod8">
                Agile Program Management
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2303" data-mp="s2dwrsyrazsz">
                Agile delivery for iterative development and continuous value
                delivery. Our certified Scrum Masters and SAFe Program
                Consultants lead Agile Release Trains, PI Planning, and
                cross-team coordination.
              </p>
              <div className="mb-6 flex-grow" data-id="element-2304" data-mp="33jphznnvk95s">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2305" data-mp="2pqzypqzqdicd">Services:</h4>
                <ul className="space-y-1 text-sm text-gray-600 list-disc ml-4" data-id="element-2306" data-mp="2powx09p7u0rt">
                  <li data-id="element-2307" data-mp="2tsl64wthx4f6">Scrum Master and Product Owner services</li>
                  <li data-id="element-2308" data-mp="3hhnt8k6wfph2">SAFe (Scaled Agile Framework) implementation</li>
                  <li data-id="element-2309" data-mp="3mgqr9kogovfj">Sprint planning, daily standups, retrospectives</li>
                  <li data-id="element-2310" data-mp="3meweitncg9mj">Backlog management and prioritization</li>
                  <li data-id="element-2311" data-mp="38gfygc3ld6rq">Velocity tracking and burn-down reporting</li>
                </ul>
              </div>
              <div className="mb-6" data-id="element-2312" data-mp="3dezpus0yfarh">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2313" data-mp="3di593qmwjifa">
                  Certifications:
                </h4>
                <p className="text-sm text-gray-600" data-id="element-2314" data-mp="220vw9mya3omy">CSM, SAFe SPC, PMI-ACP</p>
              </div>
              <Link to="/services/program-and-project-management/agile" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-2315" data-mp="2hais58kea58f">
                Learn More About Agile PM →
              </Link>
            </div>

            {/* Traditional */}
            <div className="card flex flex-col h-full" data-id="element-2316" data-mp="27linb8j7ofmb">
              <CalendarDaysIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2317" data-mp="23jxfw2pg4tji" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2318" data-mp="309oae1xqmgpb">
                Traditional Program Management
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2319" data-mp="2va1z3y1gzaj5">
                Waterfall and hybrid program management for complex, phase-gated
                programs with strict regulatory or contractual requirements.
                PMP-certified program managers deliver on-time, on-budget
                results.
              </p>
              <div className="mb-6 flex-grow" data-id="element-2320" data-mp="1fuut6gfygai5">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2321" data-mp="1frgp93rn7t8l">Services:</h4>
                <ul className="space-y-1 text-sm text-gray-600 list-disc ml-4" data-id="element-2322" data-mp="1atf329yx7dsw">
                  <li data-id="element-2323" data-mp="3mjd5db2knpd2">Program charter and planning</li>
                  <li data-id="element-2324" data-mp="3rgwx9ds700nh">Integrated master schedule (IMS) development</li>
                  <li data-id="element-2325" data-mp="3rf2arv19w0ha">Budget tracking and earned value management (EVM)</li>
                  <li data-id="element-2326" data-mp="3vh71om0c6j08">Risk and issue management (RAID logs)</li>
                  <li data-id="element-2327" data-mp="44m6kb1erglg">Stakeholder communication plans</li>
                </ul>
              </div>
              <div className="mb-6" data-id="element-2328" data-mp="93oz4w07bucj">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2329" data-mp="92nmwo7g91t8">
                  Certifications:
                </h4>
                <p className="text-sm text-gray-600" data-id="element-2330" data-mp="e4d12cpyxvlj">
                  PMP, PgMP, PRINCE2 Practitioner
                </p>
              </div>
              <Link to="/contact-us" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-2331" data-mp="n3gjdehba6yz">
                Learn More About Traditional PM →
              </Link>
            </div>

            {/* PMO */}
            <div className="card flex flex-col h-full" data-id="element-2332" data-mp="2otjpfc4lr2nc">
              <NetworkIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2333" data-mp="2ow4pbvlcb8ig" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2334" data-mp="3s5qx7w1me4nm">
                PMO Establishment & Operations
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2335" data-mp="3n8h2q1zdj2sx">
                Stand up or optimize your Program Management Office. We design
                governance frameworks, implement tools, and operate PMOs for
                federal agencies, contractors, and enterprises.
              </p>
              <div className="mb-6 flex-grow" data-id="element-2336" data-mp="ewukf1b4n5mo">
                <h4 className="font-bold text-nrs-navy mb-2" data-id="element-2337" data-mp="a049sti3d28n">Services:</h4>
                <ul className="space-y-1 text-sm text-gray-600 list-disc ml-4" data-id="element-2338" data-mp="ooik1phu7sz8">
                  <li data-id="element-2339" data-mp="oql6y0np15zq">PMO charter and governance framework</li>
                  <li data-id="element-2340" data-mp="jqgtyklzute9">
                    Tool selection and implementation (Jira, MS Project,
                    ServiceNow SPM)
                  </li>
                  <li data-id="element-2341" data-mp="sq3vd6eq3z7b">Portfolio management and prioritization</li>
                  <li data-id="element-2342" data-mp="srx2snx5ungd">Resource capacity planning</li>
                  <li data-id="element-2343" data-mp="1cbdup6kbeu1m">Metrics dashboards and reporting</li>
                </ul>
              </div>
              <Link to="/services/program-and-project-management/pmo" className="text-nrs-orange font-bold hover:underline mt-auto" data-id="element-2344" data-mp="1hbi8upxr3coo">
                Learn More About PMO Services →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CLASSIFIED ENVIRONMENT EXPERTISE */}
      <section className="section-light-blue" data-id="element-2345" data-mp="tl4doqjvcxpv">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2346" data-mp="tjb9dhwqapl1">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2347" data-mp="ycw676ml5din">
            Program Management in Secure Environments
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2348" data-mp="jldjkpz80kqb"></div>
          <p className="text-center text-lg text-gray-600 mb-12 max-w-3xl mx-auto" data-id="element-2349" data-mp="omkrcp1d5jwz">
            New River Systems program managers hold security clearances and have
            extensive experience delivering programs in secure government
            environments.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-2350" data-mp="v2bjeby6naru">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200" data-id="element-2351" data-mp="q41n1y0q244n">
              <h3 className="text-2xl font-bold mb-6 text-nrs-navy border-b border-gray-200 pb-4" data-id="element-2352" data-mp="l56tq254ox6a">
                Challenges We Solve
              </h3>
              <ul className="space-y-3 text-gray-600" data-id="element-2353" data-mp="h3bvx7mep1x9">
                <li className="flex items-start gap-3" data-id="element-2354" data-mp="c5acgll2j0l9">
                  <LockIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2355" data-mp="c6v6ma3h0hil" />{' '}
                  Air-gapped networks and limited tooling
                </li>
                <li className="flex items-start gap-3" data-id="element-2356" data-mp="2fqsfclmd0ot">
                  <LockIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2357" data-mp="3tljqzuddiqzs" />{' '}
                  Multi-domain program coordination
                </li>
                <li className="flex items-start gap-3" data-id="element-2358" data-mp="3pivkvug1ny6i">
                  <LockIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2359" data-mp="3kkbv1iyby28v" />{' '}
                  Secure Agile ceremonies in controlled environments
                </li>
                <li className="flex items-start gap-3" data-id="element-2360" data-mp="251e0ogn9ntce">
                  <LockIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2361" data-mp="24zt4ynp7u4cy" />{' '}
                  Secure artifact management and documentation
                </li>
                <li className="flex items-start gap-3" data-id="element-2362" data-mp="2e111b0868v4e">
                  <LockIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2363" data-mp="1hcbin7opzyhd" />{' '}
                  Cross-domain data synchronization
                </li>
                <li className="flex items-start gap-3" data-id="element-2364" data-mp="1ldwpocekpijn">
                  <LockIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2365" data-mp="1qchr2f5ka89o" />{' '}
                  Cleared team member availability and scheduling
                </li>
              </ul>
            </div>

            <div className="bg-nrs-navy p-8 rounded-lg shadow-sm text-white" data-id="element-2366" data-mp="xn107lxlazfk">
              <h3 className="text-2xl font-bold mb-6 text-white border-b border-white/20 pb-4" data-id="element-2367" data-mp="12mnjod40hbwt">
                Our Approach
              </h3>
              <ul className="space-y-3 text-blue-100" data-id="element-2368" data-mp="2x5zy6qylhh2m">
                <li className="flex items-start gap-3" data-id="element-2369" data-mp="3173d9b8tj92s">
                  <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2370" data-mp="319xuktwjcdrz" />{' '}
                  Cleared program managers embedded on-site
                </li>
                <li className="flex items-start gap-3" data-id="element-2371" data-mp="3b5ituka7yzy1">
                  <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2372" data-mp="2disimptpu5my" />{' '}
                  Approved secure collaboration tools (chat, wikis, dashboards)
                </li>
                <li className="flex items-start gap-3" data-id="element-2373" data-mp="2ngq455k6xobv">
                  <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2374" data-mp="2nf59zh1sg7ej" />{' '}
                  Simplified, security-compliant Agile practices
                </li>
                <li className="flex items-start gap-3" data-id="element-2375" data-mp="1uqzout6toge6">
                  <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2376" data-mp="1zqvqils1rvqk" />{' '}
                  Physical boards and manual tracking where tools unavailable
                </li>
                <li className="flex items-start gap-3" data-id="element-2377" data-mp="1n89yyl3m7e59">
                  <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2378" data-mp="1n9kyg8q11zqk" />{' '}
                  Regular classification reviews and spillage prevention
                </li>
                <li className="flex items-start gap-3" data-id="element-2379" data-mp="1d92bv672w54y">
                  <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-2380" data-mp="198ieq2mi91it" />{' '}
                  Cleared contractor teaming and coordination
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAM TYPES WE MANAGE */}
      <section className="section-white" data-id="element-2381" data-mp="zgcksncp0a0z">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2382" data-mp="uknk2goxsn33">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2383" data-mp="pk9kzo5bcnh7">
            Specialized Program Expertise
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-2384" data-mp="lio29m63lez3"></div>
          <p className="text-center text-lg text-gray-600 mb-12 max-w-3xl mx-auto" data-id="element-2385" data-mp="ay2k2pl750lw">
            Our program managers have deep domain expertise in federal IT
            program types:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-2386" data-mp="1xnu3cxaexc2">
            <div className="space-y-4" data-id="element-2387" data-mp="6w7jxof04t9p">
              <div className="flex items-start gap-3" data-id="element-2388" data-mp="6x8wjxbo1ntj">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2389" data-mp="uls0k0noneft" />
                <div data-id="element-2390" data-mp="unwelbfapttt">
                  <span className="font-bold text-nrs-navy block" data-id="element-2391" data-mp="yndiw1izkkxe">
                    ServiceNow Platform Implementations
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2392" data-mp="kv734gjqwfyh">
                    ITSM, HRSD, SPM, SecOps, GRC full lifecycle programs
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-2393" data-mp="1djw0q5lle35h">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2394" data-mp="1ib49czf3lkhl" />
                <div data-id="element-2395" data-mp="1id79ic9ysu01">
                  <span className="font-bold text-nrs-navy block" data-id="element-2396" data-mp="1rpxywzudkqqv">
                    ICAM Programs
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2397" data-mp="1nnjgggumbt04">
                    Identity platform deployments, Zero Trust rollouts, PIV/CAC
                    integration
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-2398" data-mp="1wizgjflyv4e5">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2399" data-mp="18qk28a59h1wo" />
                <div data-id="element-2400" data-mp="14q05v8sii2bl">
                  <span className="font-bold text-nrs-navy block" data-id="element-2401" data-mp="14qs0agadhu0a">
                    Cloud Migration Programs
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2402" data-mp="1inewggui4g55">
                    AWS, Azure, on-prem to cloud, hybrid architectures
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-2403" data-mp="2u2tulnyg5e1u">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2404" data-mp="2p6d5aqfigtn6" />
                <div data-id="element-2405" data-mp="yopzp4y2iapc">
                  <span className="font-bold text-nrs-navy block" data-id="element-2406" data-mp="yp9lwxur3l96">
                    IT Modernization
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2407" data-mp="13o2waheqqw54">
                    Legacy system replacement, application rationalization, tech
                    stack upgrades
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4" data-id="element-2408" data-mp="1hnu8qdpm04h0">
              <div className="flex items-start gap-3" data-id="element-2409" data-mp="1hmsszvxnetsy">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2410" data-mp="1mlvoaonttlnq" />
                <div data-id="element-2411" data-mp="1rd5crlxn2iy1">
                  <span className="font-bold text-nrs-navy block" data-id="element-2412" data-mp="1rclycf776cor">
                    Cybersecurity Programs
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2413" data-mp="1wbp2lxfcsevo">
                    RMF/ATO, continuous monitoring, CMMC compliance, SOC
                    operations
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-2414" data-mp="25ak631r0java">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2415" data-mp="3pvqoqn407xnj" />
                <div data-id="element-2416" data-mp="3kvl43qw5jbey">
                  <span className="font-bold text-nrs-navy block" data-id="element-2417" data-mp="3kv30g975tux0">
                    Data Center Consolidation
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2418" data-mp="3gw3waqt9626m">
                    Server virtualization, workload migration, data center
                    closures
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-2419" data-mp="8dxqfdqu3e2h">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2420" data-mp="8gk8i7bcpbyy" />
                <div data-id="element-2421" data-mp="3m68tspnoioh">
                  <span className="font-bold text-nrs-navy block" data-id="element-2422" data-mp="3lmo648mfg6r">
                    Software Development Programs
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2423" data-mp="w4jzn8ckr4e2">
                    Custom application development, COTS integration
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-2424" data-mp="1a923x96s3if7">
                <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0" data-id="element-2425" data-mp="1aac2dzhjmsmd" />
                <div data-id="element-2426" data-mp="1f7c2ltrpp15k">
                  <span className="font-bold text-nrs-navy block" data-id="element-2427" data-mp="1farg4s5qm24v">
                    Network Modernization
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-2428" data-mp="15ilrnsep22uf">
                    WAN upgrades, SD-WAN, zero trust network access (ZTNA)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM CERTIFICATIONS */}
      <section className="section-navy" data-id="element-2429" data-mp="iinupq9pmu9v">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2430" data-mp="ijphut40377y">
          <h2 className="text-3xl font-bold text-center mb-4 text-white" data-id="element-2431" data-mp="mjg88vpm7lby">
            Our Program Management Team
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-2432" data-mp="2cxqdz6861g4c"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-2433" data-mp="2gwzc0na8o05p">
            <div data-id="element-2434" data-mp="5adwms2uwjvb">
              <h3 className="text-2xl font-bold mb-6 text-nrs-orange" data-id="element-2435" data-mp="59vszadv73dd">
                Certifications
              </h3>
              <div className="grid grid-cols-2 gap-3 text-blue-100" data-id="element-2436" data-mp="1ad5c6unse6u">
                <div className="flex items-center gap-2" data-id="element-2437" data-mp="f108qcbd6s99">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2438" data-mp="f2jo80p74z9z" /> PMP
                </div>
                <div className="flex items-center gap-2" data-id="element-2439" data-mp="a8fbt1x5vz1i">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2440" data-mp="oxv10dh1nwf3" /> PgMP
                </div>
                <div className="flex items-center gap-2" data-id="element-2441" data-mp="k0csgo6xvj7m">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2442" data-mp="k1w7yckrtq8c" /> CSM
                </div>
                <div className="flex items-center gap-2" data-id="element-2443" data-mp="xx8ctcsn4x2z">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2444" data-mp="t1az7e8ri4tw" /> CSPO
                </div>
                <div className="flex items-center gap-2" data-id="element-2445" data-mp="2zrqvwm1p8mfz">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2446" data-mp="2urmdgqh8dhn4" /> SAFe SPC
                </div>
                <div className="flex items-center gap-2" data-id="element-2447" data-mp="2utz21mhhuq95">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2448" data-mp="29v5al301s7y1" /> SAFe POPM
                </div>
                <div className="flex items-center gap-2" data-id="element-2449" data-mp="2dw7dbtt6b24r">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2450" data-mp="2ioigr6n4h622" /> PMI-ACP
                </div>
                <div className="flex items-center gap-2" data-id="element-2451" data-mp="2nn20r1i5o7rs">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2452" data-mp="2nmjvjfdimj7q" /> PRINCE2
                </div>
                <div className="flex items-center gap-2" data-id="element-2453" data-mp="1qyvqnz2gsn64">
                  <CheckIcon className="h-4 w-4 text-nrs-orange" data-id="element-2454" data-mp="1uxw6r9tk5bpf" /> ITIL
                  Foundation
                </div>
              </div>
            </div>

            <div data-id="element-2455" data-mp="2003okg12jlzo">
              <h3 className="text-2xl font-bold mb-6 text-nrs-orange" data-id="element-2456" data-mp="24wkcpa89q0cr">
                Clearances & Experience
              </h3>
              <ul className="space-y-3 text-blue-100" data-id="element-2457" data-mp="3bbuqgg86pjdy">
                <li className="flex items-center gap-3" data-id="element-2458" data-mp="3bfa9tv8w5el6">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-2459" data-mp="3gaplqhnc90ey" /> Active
                  security clearances
                </li>
                <li className="flex items-center gap-3" data-id="element-2460" data-mp="14qhyonj94i8f">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-2461" data-mp="zqvl2czigzz3" /> 20+ years
                  average experience
                </li>
                <li className="flex items-center gap-3" data-id="element-2462" data-mp="uwrazkuy8bts">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-2463" data-mp="pypw7c4k2yo4" /> Federal
                  acquisition expertise (FAR, DFARS)
                </li>
                <li className="flex items-center gap-3" data-id="element-2464" data-mp="lvt7r4sc48c9">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-2465" data-mp="gy9ua73gnzld" /> DoD and
                  federal agency experience
                </li>
                <li className="flex items-center gap-3" data-id="element-2466" data-mp="c1t5daofth78">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-2467" data-mp="726sj2ciu2yy" /> CMMI Level
                  3+ process maturity
                </li>
                <li className="flex items-center gap-3" data-id="element-2468" data-mp="26bwqjpg6i4ut">
                  <CheckIcon className="h-5 w-5 text-nrs-orange" data-id="element-2469" data-mp="21fze3u81dinb" /> Continuous
                  ATO program experience
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY NRS */}
      <section className="section-light-blue" data-id="element-2470" data-mp="2psha2f6wkmuq">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2471" data-mp="2kywbakxhjouh">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2472" data-mp="2gycexjkqkp9e">
            Why Choose New River Systems for Program Management?
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-10" data-id="element-2473" data-mp="2bzsqncio71dv"></div>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2474" data-mp="26zn4gbv66736">
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-2475" data-mp="220aaz89sm1rm">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-2476" data-mp="1xzi1cdx0duut" />
              <div data-id="element-2477" data-mp="1y1l0bng52ybo">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-2478" data-mp="971imbn2t8n">
                  Cleared Teams
                </span>
                <span className="text-gray-600 text-sm" data-id="element-2479" data-mp="3sekm5yb44ore">
                  Cleared program managers for secure government programs
                </span>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-2480" data-mp="1j3jlaypbyh73">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-2481" data-mp="1nv1jxg3vs5ow" />
              <div data-id="element-2482" data-mp="1rwn4zoqkjqa6">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-2483" data-mp="1rx51mmh586im">
                  Proven Delivery
                </span>
                <span className="text-gray-600 text-sm" data-id="element-2484" data-mp="v8gsygrbhvlv">
                  Programs from $1M to $50M+ delivered on-time and on-budget
                </span>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-2485" data-mp="145r8wff2y8x4">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-2486" data-mp="1976r7zpvi1bt" />
              <div data-id="element-2487" data-mp="2flft4z0y8tu8">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-2488" data-mp="2flxxkixrmed8">
                  Agile + Traditional
                </span>
                <span className="text-gray-600 text-sm" data-id="element-2489" data-mp="kjb1l6yhfw45">
                  Hybrid approaches tailored to program needs and constraints
                </span>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-md shadow-sm" data-id="element-2490" data-mp="gk253uc25k4w">
              <CheckIcon className="h-6 w-6 text-nrs-orange flex-shrink-0 mt-1" data-id="element-2491" data-mp="ufebiyzksz1n" />
              <div data-id="element-2492" data-mp="pixhjo5p3qgn">
                <span className="text-lg font-bold text-nrs-navy block mb-1" data-id="element-2493" data-mp="pgkyf7o7ba20">
                  Domain Expertise
                </span>
                <span className="text-gray-600 text-sm" data-id="element-2494" data-mp="3szmjsxy8we0i">
                  Deep knowledge of ServiceNow, ICAM, cloud, and federal IT
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-white border-t border-gray-200" data-id="element-2495" data-mp="6ptgviu28dcj">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-2496" data-mp="3f6eu5btq5slg">
          <h3 className="text-3xl font-bold mb-4 text-nrs-navy" data-id="element-2497" data-mp="3a9elct5hqbqn">
            Let's Deliver Your Mission-Critical Program
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-2498" data-mp="1pp9krw7xnbpm">
            Our program managers are ready to lead your next federal IT
            initiative.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2499" data-mp="1gr78ulmnuynk">
            <Link to="/contact-us" className="btn-primary" data-id="element-2500" data-mp="1kqpxnshlrtvo">
              Discuss Your Program Needs
            </Link>
            <Link to="/contact-us" className="btn-secondary" data-id="element-2501" data-mp="28idefhl5hok8">
              Request a Program Assessment
            </Link>
          </div>
        </div>
      </section>
    </div>;
}